// Group-centric browse view for the Concept Intelligence drawer.
// Two synchronised views share the same search + source filter:
//   1. Accordion: group cards with collapsible concept lists
//   2. Table:    flat DataTable with Group as a first-class column
// The "this concept belongs to this group" relationship is surfaced in
// every row, in either view, so the flow reads as group → concept.
import {
  ChevronDown,
  ChevronRight,
  FolderTree,
  LayoutGrid,
  Layers,
  Search,
  Table,
  X as XIcon,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DataTable } from "../../../components/DataTable";
import type { ConceptFinding } from "../api/contentRegistryApi";
import { EMPTY_PLACEHOLDER } from "../constants";
import { plainText } from "../utils";
import { SectionCard } from "../Domain/domainIntelligenceParts";
import {
  ConceptConfidenceBar,
  ConceptIdChip,
  EmptySlot,
  SectionChip,
} from "./conceptIntelligenceParts";
import { SectionFilterChip } from "./conceptSectionFilterChip";
import {
  CONCEPT_GROUPS_COPY,
  CONCEPT_ROW_CLS,
  CONCEPT_ROW_META_CLS,
  CONCEPT_SOURCE_FILTER_LABELS,
  CONCEPT_TABLE_MAX_HEIGHT,
  CONCEPT_TABLE_PAGE_SIZE,
  CONCEPT_VIEW_MODE,
  CONCEPT_VIEW_MODE_LABELS,
  CONCEPT_VIEW_MODE_ORDER,
  CONCEPT_VIEW_TOGGLE_LABEL,
  DEFINED_SEARCH_INPUT_CLS,
  GROUP_CARD_CLS,
  GROUP_DESCRIPTION_CLS,
  GROUP_HEADER_BUTTON_CLS,
  GROUP_HEADER_CLS,
  GROUP_HEADER_UNCLASSIFIED_CLS,
  GROUP_META_PILL_CLS,
  GROUP_META_STRONG_CLS,
  GROUP_NAME_CLS,
  SOURCE_BADGE_DEFINED_CLS,
  SOURCE_BADGE_REFERENCED_CLS,
  conceptTableColumns,
  filterConceptGroups,
  isDefinedSource,
  totalConceptsAcrossGroups,
  type ConceptGroup,
  type ConceptSourceFilter,
  type ConceptViewMode,
} from "./utilities";

export interface ConceptGroupsSectionProps {
  groups: ConceptGroup[];
  searchQuery: string;
  sourceFilter: ConceptSourceFilter;
  viewMode: ConceptViewMode;
  collapsedGroups: Set<string>;
  tablePage: number;
  onSearchChange: (value: string) => void;
  onSourceFilterChange: (value: ConceptSourceFilter) => void;
  onViewModeChange: (value: ConceptViewMode) => void;
  onToggleGroup: (groupName: string) => void;
  onTablePageChange: (page: number) => void;
}

const SOURCE_FILTER_ORDER: ConceptSourceFilter[] = [
  "all",
  "defined",
  "referenced",
];

export function ConceptGroupsSection({
  groups,
  searchQuery,
  sourceFilter,
  viewMode,
  collapsedGroups,
  tablePage,
  onSearchChange,
  onSourceFilterChange,
  onViewModeChange,
  onToggleGroup,
  onTablePageChange,
}: ConceptGroupsSectionProps) {
  const filteredGroups = filterConceptGroups(groups, searchQuery, sourceFilter);
  const visibleConceptCount = totalConceptsAcrossGroups(filteredGroups);
  const totalConceptCount = totalConceptsAcrossGroups(groups);
  const rightLabel = `${filteredGroups.length} of ${groups.length} groups · ${visibleConceptCount} of ${totalConceptCount} concepts`;

  const renderBody = () => {
    if (groups.length === 0) {
      return <EmptySlot label="concept groups" />;
    }
    if (filteredGroups.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
          {CONCEPT_GROUPS_COPY.emptyText}
        </div>
      );
    }
    if (viewMode === CONCEPT_VIEW_MODE.TABLE) {
      return (
        <TableView
          groups={filteredGroups}
          page={tablePage}
          onPageChange={onTablePageChange}
        />
      );
    }
    return (
      <AccordionView
        groups={filteredGroups}
        collapsedGroups={collapsedGroups}
        onToggleGroup={onToggleGroup}
      />
    );
  };

  return (
    <SectionCard headerLabel={CONCEPT_GROUPS_COPY.header} rightLabel={rightLabel}>
      <div className="flex flex-col gap-3 p-3">
        <GroupsToolbar
          searchQuery={searchQuery}
          sourceFilter={sourceFilter}
          viewMode={viewMode}
          onSearchChange={onSearchChange}
          onSourceFilterChange={onSourceFilterChange}
          onViewModeChange={onViewModeChange}
        />
        {renderBody()}
      </div>
    </SectionCard>
  );
}

interface GroupsToolbarProps {
  searchQuery: string;
  sourceFilter: ConceptSourceFilter;
  viewMode: ConceptViewMode;
  onSearchChange: (value: string) => void;
  onSourceFilterChange: (value: ConceptSourceFilter) => void;
  onViewModeChange: (value: ConceptViewMode) => void;
}

function GroupsToolbar({
  searchQuery,
  sourceFilter,
  viewMode,
  onSearchChange,
  onSourceFilterChange,
  onViewModeChange,
}: GroupsToolbarProps) {
  const clearSearch = () => onSearchChange("");

  return (
    <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="relative w-full sm:w-80">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
        <Input
          type="text"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={CONCEPT_GROUPS_COPY.searchPlaceholder}
          className={DEFINED_SEARCH_INPUT_CLS}
        />
        {searchQuery && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearSearch}
            className="absolute right-2 top-1/2 size-6 -translate-y-1/2 p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Clear search"
          >
            <XIcon className="size-3.5" />
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            {CONCEPT_GROUPS_COPY.sourceFilterLabel}
          </span>
          {SOURCE_FILTER_ORDER.map((key) => (
            <SectionFilterChip
              key={key}
              active={sourceFilter === key}
              label={CONCEPT_SOURCE_FILTER_LABELS[key]}
              onClick={() => onSourceFilterChange(key)}
            />
          ))}
        </div>
        <ViewModeToggle
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />
      </div>
    </div>
  );
}

interface ViewModeToggleProps {
  viewMode: ConceptViewMode;
  onViewModeChange: (value: ConceptViewMode) => void;
}

function ViewModeToggle({ viewMode, onViewModeChange }: ViewModeToggleProps) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
        {CONCEPT_VIEW_TOGGLE_LABEL}
      </span>
      {CONCEPT_VIEW_MODE_ORDER.map((mode) => (
        <SectionFilterChip
          key={mode}
          active={viewMode === mode}
          label={CONCEPT_VIEW_MODE_LABELS[mode]}
          onClick={() => onViewModeChange(mode)}
        />
      ))}
    </div>
  );
}

interface AccordionViewProps {
  groups: ConceptGroup[];
  collapsedGroups: Set<string>;
  onToggleGroup: (groupName: string) => void;
}

function AccordionView({
  groups,
  collapsedGroups,
  onToggleGroup,
}: AccordionViewProps) {
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <GroupCard
          key={group.name}
          group={group}
          expanded={!collapsedGroups.has(group.name)}
          onToggle={() => onToggleGroup(group.name)}
        />
      ))}
    </div>
  );
}

interface TableViewProps {
  groups: ConceptGroup[];
  page: number;
  onPageChange: (page: number) => void;
}

function TableView({ groups, page, onPageChange }: TableViewProps) {
  const flat = groups.flatMap((g) => g.concepts);
  const rowKey = (row: ConceptFinding) =>
    row.concept_id ?? `${row.group}-${row.name}`;

  if (flat.length <= CONCEPT_TABLE_PAGE_SIZE) {
    return (
      <DataTable
        columns={conceptTableColumns}
        data={flat}
        compact
        maxHeight={CONCEPT_TABLE_MAX_HEIGHT}
        rowKey={rowKey}
      />
    );
  }
  const paged = flat.slice(
    page * CONCEPT_TABLE_PAGE_SIZE,
    (page + 1) * CONCEPT_TABLE_PAGE_SIZE,
  );
  return (
    <DataTable
      columns={conceptTableColumns}
      data={paged}
      compact
      maxHeight={CONCEPT_TABLE_MAX_HEIGHT}
      page={page}
      pageSize={CONCEPT_TABLE_PAGE_SIZE}
      totalItems={flat.length}
      onPageChange={onPageChange}
      itemLabel="concepts"
      pagerStyle="simple"
      rowKey={rowKey}
    />
  );
}

interface GroupCardProps {
  group: ConceptGroup;
  expanded: boolean;
  onToggle: () => void;
}

function GroupCard({ group, expanded, onToggle }: GroupCardProps) {
  const headerCls = group.isUnclassified
    ? GROUP_HEADER_UNCLASSIFIED_CLS
    : GROUP_HEADER_CLS;
  const displayName = group.isUnclassified
    ? CONCEPT_GROUPS_COPY.ungroupedLabel
    : plainText(group.name);
  const description = group.description
    ? plainText(group.description)
    : group.isUnclassified
      ? CONCEPT_GROUPS_COPY.ungroupedDescription
      : "";

  return (
    <section className={GROUP_CARD_CLS}>
      <div className={headerCls}>
        <Button
          type="button"
          variant="ghost"
          size="none"
          onClick={onToggle}
          aria-expanded={expanded}
          className={GROUP_HEADER_BUTTON_CLS}
        >
          <div className="flex items-start gap-2.5 min-w-0 flex-1">
            <span className="grid size-8 flex-none place-items-center rounded-md bg-background border border-border">
              {expanded ? (
                <ChevronDown className="size-4 text-indigo-600 dark:text-indigo-400" />
              ) : (
                <ChevronRight className="size-4 text-indigo-600 dark:text-indigo-400" />
              )}
            </span>
            <span className="grid size-8 flex-none place-items-center rounded-md bg-background border border-border">
              <FolderTree className="size-4 text-indigo-600 dark:text-indigo-400" />
            </span>
            <div className="min-w-0 flex flex-col gap-0.5">
              <div className={GROUP_NAME_CLS}>{displayName}</div>
              {description && (
                <p className={GROUP_DESCRIPTION_CLS}>{description}</p>
              )}
            </div>
          </div>
        </Button>
        <GroupMetaPills group={group} />
      </div>
      {expanded && (
        <ul>
          {group.concepts.map((concept) => (
            <li key={concept.concept_id || concept.name}>
              <ConceptRow concept={concept} groupName={displayName} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function GroupMetaPills({ group }: { group: ConceptGroup }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 justify-end shrink-0">
      <span className={GROUP_META_STRONG_CLS}>
        <Layers className="size-3" />
        {group.concepts.length}
        {group.concepts.length === 1 ? " concept" : " concepts"}
      </span>
      {group.definedCount > 0 && (
        <span className={GROUP_META_PILL_CLS}>
          {group.definedCount} defined
        </span>
      )}
      {group.referencedCount > 0 && (
        <span className={GROUP_META_PILL_CLS}>
          {group.referencedCount} referenced
        </span>
      )}
      {group.totalOccurrences > 0 && (
        <span className={GROUP_META_PILL_CLS}>
          {group.totalOccurrences.toLocaleString()} mentions
        </span>
      )}
    </div>
  );
}

interface ConceptRowProps {
  concept: ConceptFinding;
  groupName: string;
}

function ConceptRow({ concept, groupName }: ConceptRowProps) {
  const defined = isDefinedSource(concept.source);
  const sourceLabel = defined ? "Defined" : "Referenced";
  const sourceCls = defined
    ? SOURCE_BADGE_DEFINED_CLS
    : SOURCE_BADGE_REFERENCED_CLS;
  const occurrences = concept.occurrences ?? 0;
  const occurrenceLabel = occurrences === 1 ? "mention" : "mentions";
  const description = plainText(concept.description) || EMPTY_PLACEHOLDER;

  return (
    <div className={CONCEPT_ROW_CLS}>
      <ConceptIdChip id={concept.concept_id} />
      <div className="min-w-0 flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-foreground break-words">
            {plainText(concept.name)}
          </span>
          <span className={sourceCls}>{sourceLabel}</span>
        </div>
        <p className="text-[12px] text-muted-foreground break-words leading-snug">
          {description}
        </p>
        <div className={CONCEPT_ROW_META_CLS}>
          <span>
            {CONCEPT_GROUPS_COPY.belongsToPrefix}{" "}
            <span className="font-semibold text-foreground">{groupName}</span>
          </span>
          <span>·</span>
          <SectionChip section={concept.section} />
          {concept.page !== null && concept.page !== undefined && (
            <>
              <span>·</span>
              <span>p.{concept.page}</span>
            </>
          )}
          <span>·</span>
          <span>
            {occurrences.toLocaleString()} {occurrenceLabel}
          </span>
        </div>
      </div>
      <div className="w-[140px] flex-none">
        <ConceptConfidenceBar conf={concept.confidence} />
      </div>
    </div>
  );
}
